import React, {
  forwardRef,
  useState,
  useImperativeHandle,
  useEffect,
} from "react";
import Styles from "./index.module.css";
import Image from "next/image";
import ProductCard from "./ProductCard";
import TertiaryBtn from "@/components/button/tertiaryBtn";
import PrimaryBtn from "@/components/button/primaryBtn";
import { removeCartProduct, addToCart } from "@/api/cart";
import { useRouter } from "next/router";
import NotFound from "@/components/error/NotFound";
import { successToast, warningToast } from "@/utils/toast";
import en from "@/localization/en.json";
import InlineLoader from "@/icons/inline-loader.svg";

function CartSidebar({ cartItemsCount, data, isLoading }, ref) {
  const [cartSidebarVisibilityClass, setCartSidebarVisibilityClass] =
    useState("hidden");
  const [cartSidebarAnimationClass, setCartSidebarAnimationClass] =
    useState("translate-x-[100%]");

  const [cartData, setCartData] = useState(() => data);

  const router = useRouter();

  useImperativeHandle(ref, () => ({
    makeCartSidebarVisible: () => {
      setCartSidebarVisibilityClass("block");
      setCartSidebarAnimationClass("translate-x-[0]");
    },
  }));

  const makeCartSidebarhidden = () => {
    setCartSidebarVisibilityClass("hidden");
    setCartSidebarAnimationClass("translate-x-[100%]");
  };

  useEffect(() => {
    setCartData(data);
  }, [data]);

  const handleQtyUpdate = async (data) => {
    let updatedProduct = {
      product: data.slug,
      quantity: data.quantity,
      price: data.unit_price,
      from_cart: true,
    };

    if (data.variation) {
      updatedProduct.variation = data?.variation?.variation;
    }

    const res = await addToCart(updatedProduct);

    if (res) {
      successToast(en["myCart.quantity_updated"]);
      //setCartTableData(res?.data?.cart?.products);
      setCartData(res?.data?.cart);
    }
    if (res?.data.warning) {
      warningToast(res.data.warning);
    }
  };

  const handleRemoveCartItem = async (productData) => {
    let removedProduct = {
      product: productData?.slug,
    };

    if (productData.variation) {
      removedProduct.variation = productData.variation.variation;
    }

    const res = await removeCartProduct(removedProduct);

    if (!res) return;
    if (res?.data.warning) {
      warningToast(res.data.warning);
    }

    setCartData(res?.data?.cart);
  };

  return (
    <>
      <div
        className={`inset-0 bg-[#1B2D49]/40 fixed z-[100] ${cartSidebarVisibilityClass}`}
        onClick={makeCartSidebarhidden}
      ></div>
      <div
        className={`fixed z-[99999] inset-y-0 right-0 bg-white w-full sm:w-[25.625rem] h-full ml-auto opacity-1 shadow-inner
      ${Styles.innerShadow} ${cartSidebarAnimationClass} ${Styles.sidebarTransition}`}
      >
        <div className="h-[10%] flex justify-between items-center border-[#EBEEF2] sm:border-b-[1px] px-[1.25rem]">
          <p className="text-heading6 md:text-heading4 text-grayD1 font-medium">
            {en["myCart.shopping_cart"]}
          </p>
          <button onClick={makeCartSidebarhidden}>
            <Image
              src="/assets/icons/x.svg"
              width={14}
              height={14}
              alt="close icon"
              className="cursor-pointer"
            />
          </button>
        </div>

        {isLoading && (
          <div className="h-[90%] flex justify-center items-center">
            <InlineLoader className="inline-block h-[7rem] w-[7rem] absolute top-[calc(50%_-_3.5rem)] left-[calc(50%_-_3.5rem)]" />
          </div>
        )}

        {!isLoading && cartItemsCount > 0 && (
          <div
            className={`px-[1.25rem] py-[1.75rem] h-[68%] overflow-y-auto ${Styles.customScrollBar}`}
          >
            {cartData?.products?.map((data) => (
              <div
                className="mb-[2rem]"
                key={data.variation ? data.variation.variation : data.slug}
              >
                <ProductCard
                  product={data}
                  handleQtyUpdate={(data) => handleQtyUpdate(data)}
                  handleRemoveCartItem={(data) => handleRemoveCartItem(data)}
                />
              </div>
            ))}
          </div>
        )}

        {!isLoading && cartItemsCount > 0 ? (
          <div>
            <div className="h-[17%] border-[#EBEEF2] border-t-[1px] flex justify-between px-[1.25rem] pt-[1.375rem] mb-[2rem]">
              <p className="text-body1 text-grayD2">Total</p>

              <p className="text-heading5 flex text-grayD1 font-semibold">
                <span>&#2547;</span>
                {cartData?.total_product_price_with_discount}
              </p>
            </div>

            <div className="flex justify-between px-[1.25rem]">
              <TertiaryBtn
                btnText="View Cart"
                className="w-[48%] h-[3rem]"
                onClick={() => router.push("/my-cart")}
              />

              <PrimaryBtn
                btnText="Checkout"
                className="w-[48%] h-[3rem]"
                onClick={() => router.push("/checkout")}
              />
            </div>
          </div>
        ) : (
          !isLoading && (
            <div className="h-[90%] flex justify-center items-center">
              <NotFound
                imageHolderClassName="w-[15.5rem] md:w-[15rem] h-[11.875rem] md:h-[12.375rem]"
                imageName="empty-cart.png"
                wrapperClassName="max-w-[14.375rem]"
                bodyText="Your cart is empty! Start shopping"
                bodyClassName="text-grayD4 text-body2 mt-[1.25rem]"
              />
            </div>
          )
        )}
      </div>
    </>
  );
}

export default forwardRef(CartSidebar);
